<template>
    <div>
        <div class="FileInput form-group">
            <label
                :for="id"
                :class="{
                    'FileInput-label--draggedOver': draggedOver,
                    'FileInput-label--filled': value && value.slug
                }"
                :data-qa="`${qa}.label`"
                class="FileInput-label p-2 font-weight-bold"
                @dragover.prevent="draggedOver = true"
                @dragleave.prevent="draggedOver = false"
                @drop.prevent="uploadMedia($event.dataTransfer.files)"
            >

                <input
                    :id="id"
                    :data-qa="`${qa}.input`"
                    type="file"
                    class="FileInput-hiddenInput form-control-file"
                    @change="uploadMedia($event.target.files)"
                >
                <i
                    v-if="!uploadingMedia"
                    class="FileInput-icon fa fa-upload"
                    aria-hidden="true"
                />
                <i
                    v-if="uploadingMedia"
                    class="FileInput-icon fas fa-spinner fa-spin"
                />
                {{ label }}
            </label>

            <label
                v-if="captureLabel !== ''"
                class="
          d-flex
          p-2
          align-items-center
          justify-content-center
          col-xs-auto
          btn btn-lg btn-primary
          text-right text-white
          d-md-none
          file-input-label
        "
            >
                <input
                    :data-qa="`${qa}.input-capture`"
                    type="file"
                    class="FileInput-hiddenInput form-control-file"
                    accept="image/*"
                    capture="user"
                    @change="uploadMedia($event.target.files)"
                >
                <i
                    v-if="!uploadingMedia"
                    class="FileInput-icon fa fa-camera"
                    aria-hidden="true"
                />
                <i
                    v-else
                    class="FileInput-icon fas fa-spinner fa-spin"
                />
                {{ captureLabel }}
            </label>
        </div>
        <div v-if="value && value.name">
            <ul class="list-unstyled">
                <li class="FileInput-slug">
                    <i class="fa fa-paperclip" />
                    {{ value.name }}
                </li>
            </ul>
        </div>

        <div
            :id="'js-error-' + inputId"
            :aria-label="errorLabel || error"
            :class="{ 'FileInput-error--displayed': errorLabel !== '' || error }"
            :tabindex="getErrorTabIndex"
            class="FileInput-error"
        >
            {{ errorLabel || size.errorMessage }}
        </div>
    </div>
</template>

<script>

export default {
    props: {
        captureLabel: {
            type: String,
            default: ''
        },
        errorLabel: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        qa: {
            type: String,
            default: ''
        },
        required: {
            type: Boolean,
            default: true
        },
        size: {
            type: Object,
            default() {
                return {
                    errorMessage: '50M max',
                    max: 50
                }
            }
        },
        value: {
            type: [Object, String, File],
            default: null
        }
    },
    data () {
        return {
            error: false,
            draggedOver: false,
            uploadingMedia: false,
            inputId: null
        }
    },
    computed: {
        name () {
            return this.value && this.value.name ? this.value.name : ''
        },
        getErrorTabIndex () {
            return this.errorLabel !== '' || this.error ? '0' : '-1'
        },
    },
    watch: {
        errorLabel () {
            if (this.errorLabel !== '') {
                document.querySelector(`#js-error-${this.inputId}`).focus()
            }
        }
    },
    mounted() {
        this.inputId = this._uid
    },
    methods: {
        uploadMedia (files) {
            this.error = false

            if (files.length > 0) {
                if (files[0].size > this.size.max * 1000000) {
                    this.$emit('input', '');
                    this.error = true;
                } else {
                    this.draggedOver = false;
                    this.$emit('input', files[0]);
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.FileInput {
    &-input--hidden {
        float: left;
        width: 0;
        opacity: 0;
    }

    &-hiddenInput {
        /*
        * Hide only visually, but have it available for screen readers:
        * https://snook.ca/archives/html_and_css/hiding-content-for-accessibility
        *
        * 1. For long content, line feeds are not interpreted as spaces and small width
        *    causes content to wrap 1 word per line:
        *    https://medium.com/@jessebeach/beware-smushed-off-screen-accessible-text-5952a4c2cbfe
        */
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        border: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap; /* 1 */
        clip-path: inset(50%);
    }

    &-label {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px 0;
        border-radius: 10px;
        outline: 1px dashed;
        background: rgba(230, 0, 126, .15);
        cursor: pointer;
        font-size: large;
        color: $color-f2m-pink;

        &:hover,
        &--draggedOver,
        &:focus-within {
            outline: 2px dashed;
        }

        &--filled {
            outline: 2px solid;
        }
    }

    &-icon {
        margin-right: 10px;
    }

    &-slug {
        overflow-wrap: break-word;
    }

    &-error {
        margin-bottom: 0;
        opacity: 0;
        transition: all .3s ease-in-out;

        &--displayed {
            @include errorBlock;

            margin-top: 10px;
            opacity: 1;
        }
    }
}
</style>
